/**
 * Polls scheduler status from backend. Use when viewing the fetch-leads-count page.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useQuery } from '@tanstack/react-query';

import { useSmswiserCatalogApiEnabled } from '@/hooks/smswiser/catalog/useSmswiserCatalogApiEnabled';
import api from '@/services/api';

export interface SchedulerProgress {
  totalPending: number;
  fetched: number;
  failed: number;
  currentBatch: number;
  status: 'idle' | 'loading' | 'running' | 'paused' | 'done' | 'stopped' | 'error';
  lastResult?: { succeeded: number; failed: number };
  error?: string;
  danishTime: string;
  config?: SchedulerConfig;
}

export interface SchedulerConfig {
  startHour: number;
  endHour: number;
  batchSize: number;
  delayMinutes: number;
  delaySeconds: number;
  staleness: string;
  excludeRecent: string;
  customSinceDate?: string;
  customExcludeAfterDate?: string;
}

export interface SchedulerStatusResponse {
  isRunning: boolean;
  progress: SchedulerProgress;
  config: SchedulerConfig | null;
}

const POLL_MS = 2000;

export function useSchedulerStatus(enabled = true) {
  const queryEnabled = useSmswiserCatalogApiEnabled(enabled);
  return useQuery({
    queryKey: ['smswiser-catalog', 'scheduler-status'] as const,
    queryFn: async (): Promise<SchedulerStatusResponse> => {
      const res = await api.get<{ data: SchedulerStatusResponse }>(
        '/smswiser/catalog/scheduler/status',
      );
      return res.data.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.isRunning) return POLL_MS;
      return false;
    },
    enabled: queryEnabled,
  });
}
